/**
 * Tier 0–3 creatures, the watery start of the chain, redrawn in code after the original
 * glossy kawaii pictures (assets/merge/v2/scaled/<id>.png), as close to them as clean vector
 * shapes allow:
 *   Cell       a green cell with no face: a wavy membrane, pale cytoplasm full of organelle
 *              dots and a lime nucleus with darker spots
 *   Starfish   a fat red five-point star with a raised rim, cream dots and a small cute face
 *   Jellyfish  a pink dome with a simple face and short frilly tentacles
 *   Fish       a round yellow fish with orange fins and a wide little smile
 *
 * Style (the originals'): crisp outlines in a deeper shade of each part's own colour, two or
 * three cel tones lit from the upper left plus glossy white highlight spots, and SMALL simple
 * faces: two dark oval eyes with one white highlight each, a tiny curved smile, soft pink blush.
 * No props, no sparkly anime eyes.
 *
 * Contract (creatures/index.js drawCreature): `disc` tones (base, light, shade, ink = rim),
 * `discPattern(c, r)` inside the glass, and `draw(c, r, pose)` centred on the origin inside
 * about 0.95 r (r = the disc radius = the physics circle). pose.mood picks a kit mood (MOODS
 * in candy-kit.js) or the resting smile; "blink" closes only the eyes and keeps the mouth (the
 * Cell has no face, so every mood is the same picture). pose.noDisc is the creature layer
 * alone, which rotates in game while the bubble and its glare stay upright, so every part
 * carries its own outline. Deterministic, no shadowBlur or filters.
 *
 * The pre-candy family rules (PEN, familyFace, BLUSH_ON, …) live in sea-family-legacy.js and
 * are re-exported below unchanged for the modules that still import them from here.
 */
import { mixHex } from "../palette.js";
import { TAU, hash } from "../toon-kit.js";
import {
  rgba,
  tone,
  candyPart,
  smoothPath,
  glassBubble,
} from "./candy-kit.js";
import { capsuleSdf, smin, sdfOutline } from "./sea-candy-helpers.js";

export {
  PEN,
  ROPE_EDGE,
  GLINT_R,
  FACE,
  BLUSH_ON,
  familyFace,
  familyRope,
  creatureGlint,
  creatureFace,
  blobPath,
  starPath,
  grownPath,
  inkedRope,
  discBubbles,
  INK_SOFT,
} from "./sea-family-legacy.js";

// ---------------------------------------------------------------------------------------
// Shared paint: a toned-down candy part, highlight spots and the small kawaii face.

/** Outline reach beyond each part (r units): moderate, like the originals' line art. */
var LINE = { body: 0.024, part: 0.02, fine: 0.014 };

/**
 * One glossy part: coloured outline swelling a little to the lower right, base, a shade
 * crescent lower right, a light patch upper left and a faint rim light (candyPart, calmed).
 */
function part(c, r, path, t, R, o) {
  var opt = { line: LINE.body, swell: 0.4, rim: 0.22, bounceK: 0.8 };
  if (o) for (var k in o) if (Object.prototype.hasOwnProperty.call(o, k)) opt[k] = o[k];
  candyPart(c, r, path, t, R, opt);
}

/** A crisp white highlight spot (absolute units). */
function spot(c, x, y, rx, ry, rot, a) {
  c.fillStyle = "rgba(255,255,255," + (a != null ? a : 0.92) + ")";
  c.beginPath();
  c.ellipse(x, y, rx, ry, rot || 0, 0, TAU);
  c.fill();
}

/** Closed unit-point outline (r units) of an oval with a gentle wobble: [amp, freq, phase]. */
function wobblePts(cx, cy, rx, ry, waves, n) {
  var out = [];
  for (var i = 0; i < n; i++) {
    var a = (i / n) * TAU, k = 1;
    for (var w = 0; w < waves.length; w++) k += waves[w][0] * Math.sin(a * waves[w][1] + waves[w][2]);
    out.push([cx + Math.cos(a) * rx * k, cy + Math.sin(a) * ry * k]);
  }
  return out;
}

function ptsPath(pts, r) {
  return function (c) { smoothPath(c, pts, r); };
}

/** Moods → [eyes, mouth]; null mouth keeps the resting smile. Same keys as the kit's MOODS. */
var FACE_MOODS = {
  smile: ["open", "smile"],
  grin: ["open", "grin"],
  happy: ["happy", "grin"],
  wow: ["wow", "o"],
  blink: ["closed", null],
  sleepy: ["sleepy", "o"],
  fierce: ["fierce", "flat"],
  cool: ["cool", "smirk"],
  wink: ["wink", "smile"],
  proud: ["happy", "smirk"],
};

var MOUTH_IN = "#8c2434";
var TONGUE = "#ff7c96";

/**
 * The originals' face, all in r units: eyes centred on (x ± gap / 2, y) with radii ex × ey,
 * mouth = [x, y, half-width], blush = cheek centres with radii br, colours eyeInk / mouthInk /
 * blushInk, skin = lid colour for "cool", spark2 = the tiny second catchlight, clip = the head
 * path (cheeks stay on it).
 */
function kawaiiFace(c, r, f, pose) {
  var m = FACE_MOODS[(pose && pose.mood) || "smile"] || FACE_MOODS.smile;
  var eyes = m[0], mouth = m[1] || "smile";
  c.save();
  if (f.clip) {
    c.beginPath();
    f.clip(c);
    c.clip();
  }
  for (var b = 0; b < f.blush.length; b++) {
    blushAt(c, f.blush[b][0] * r, f.blush[b][1] * r, f.br[0] * r, f.br[1] * r, f.blushInk);
  }
  c.restore();
  kawaiiMouth(c, r, f.mouth[0] * r, f.mouth[1] * r, f.mouth[2] * r, mouth, f);
  for (var side = -1; side <= 1; side += 2) {
    var kind = eyes === "wink" ? (side > 0 ? "happy" : "open") : eyes;
    kawaiiEye(c, r, (f.x + side * f.gap * 0.5) * r, f.y * r, f.ex * r, f.ey * r, kind, side, f);
  }
}

/** A soft pink cheek: an oval fading out at its edge. */
function blushAt(c, x, y, rx, ry, col) {
  c.save();
  c.translate(x, y);
  c.scale(1, ry / rx);
  var g = c.createRadialGradient(0, 0, 0, 0, 0, rx);
  g.addColorStop(0, rgba(col, 0.8));
  g.addColorStop(0.6, rgba(col, 0.6));
  g.addColorStop(1, rgba(col, 0));
  c.fillStyle = g;
  c.beginPath();
  c.arc(0, 0, rx, 0, TAU);
  c.fill();
  c.restore();
}

/** A dark oval eye, a warm glow low in it, one white highlight (plus an optional tiny dot). */
function eyeOval(c, x, y, ex, ey, ink, spark2) {
  c.save();
  c.fillStyle = ink;
  c.beginPath();
  c.ellipse(x, y, ex, ey, 0, 0, TAU);
  c.fill();
  c.clip();
  c.fillStyle = rgba(mixHex(ink, "#c8584e", 0.42), 0.75);
  c.beginPath();
  c.ellipse(x, y + ey * 0.66, ex * 0.72, ey * 0.42, 0, 0, TAU);
  c.fill();
  c.restore();
  c.fillStyle = "#ffffff";
  c.beginPath();
  c.ellipse(x - ex * 0.24, y - ey * 0.3, ex * 0.44, ey * 0.34, -0.35, 0, TAU);
  c.fill();
  if (spark2) {
    c.beginPath();
    c.arc(x + ex * 0.38, y + ey * 0.4, ex * 0.16, 0, TAU);
    c.fill();
  }
}

function kawaiiEye(c, r, x, y, ex, ey, kind, side, f) {
  c.save();
  c.lineCap = "round";
  c.lineJoin = "round";
  c.strokeStyle = f.eyeInk;
  c.lineWidth = Math.max(0.017 * r, ex * 0.34);
  if (kind === "closed") {
    // A relaxed closed lid, curving down.
    c.beginPath();
    c.moveTo(x - ex * 1.1, y + ey * 0.05);
    c.quadraticCurveTo(x, y + ey * 0.95, x + ex * 1.1, y + ey * 0.05);
    c.stroke();
  } else if (kind === "sleepy") {
    c.beginPath();
    c.moveTo(x - ex * 1.1, y + ey * 0.32);
    c.quadraticCurveTo(x, y + ey * 0.72, x + ex * 1.1, y + ey * 0.32);
    c.stroke();
  } else if (kind === "happy") {
    c.beginPath();
    c.moveTo(x - ex * 1.1, y + ey * 0.4);
    c.quadraticCurveTo(x, y - ey * 0.95, x + ex * 1.1, y + ey * 0.4);
    c.stroke();
  } else {
    var k = kind === "wow" ? 1.14 : 1;
    eyeOval(c, x, y, ex * k, ey * k, f.eyeInk, f.spark2);
    if (kind === "cool") {
      // A heavy lid across the top: skin over the upper part, then the lid line.
      c.save();
      c.beginPath();
      c.rect(x - ex * 1.6, y - ey * 1.6, ex * 3.2, ey * 1.15);
      c.clip();
      c.fillStyle = f.skin;
      c.beginPath();
      c.ellipse(x, y, ex * 1.3, ey * 1.3, 0, 0, TAU);
      c.fill();
      c.restore();
      c.beginPath();
      c.moveTo(x - ex * 1.12, y - ey * 0.42);
      c.lineTo(x + ex * 1.12, y - ey * 0.48);
      c.stroke();
    } else if (kind === "fierce") {
      // A short brow dipping toward the nose.
      c.beginPath();
      c.moveTo(x - side * ex * 1.0, y - ey * 1.25);
      c.lineTo(x + side * ex * 1.0, y - ey * 1.7);
      c.stroke();
    }
  }
  c.restore();
}

function kawaiiMouth(c, r, x, y, w, kind, f) {
  c.save();
  c.lineCap = "round";
  c.lineJoin = "round";
  c.strokeStyle = f.mouthInk;
  c.lineWidth = Math.max(0.016 * r, w * 0.3);
  c.beginPath();
  if (kind === "grin") {
    var shape = function (cc) {
      cc.moveTo(x - w, y - w * 0.15);
      cc.quadraticCurveTo(x, y + w * 0.05, x + w, y - w * 0.15);
      cc.bezierCurveTo(x + w * 0.9, y + w * 1.15, x - w * 0.9, y + w * 1.15, x - w, y - w * 0.15);
      cc.closePath();
    };
    shape(c);
    c.fillStyle = MOUTH_IN;
    c.fill();
    c.save();
    c.clip();
    c.fillStyle = TONGUE;
    c.beginPath();
    c.ellipse(x, y + w * 0.95, w * 0.6, w * 0.42, 0, 0, TAU);
    c.fill();
    c.restore();
    c.beginPath();
    shape(c);
    c.stroke();
  } else if (kind === "o") {
    c.ellipse(x, y + w * 0.25, w * 0.4, w * 0.48, 0, 0, TAU);
    c.fillStyle = MOUTH_IN;
    c.fill();
    c.stroke();
  } else if (kind === "flat") {
    c.moveTo(x - w * 0.7, y + w * 0.3);
    c.quadraticCurveTo(x, y + w * 0.08, x + w * 0.7, y + w * 0.3);
    c.stroke();
  } else if (kind === "smirk") {
    c.moveTo(x - w * 0.85, y + w * 0.1);
    c.quadraticCurveTo(x + w * 0.1, y + w * 0.65, x + w * 0.9, y - w * 0.28);
    c.stroke();
  } else {
    // The resting smile: a small curve with the corners turned up.
    c.moveTo(x - w, y - w * 0.22);
    c.quadraticCurveTo(x, y + w * 0.95, x + w, y - w * 0.22);
    c.stroke();
  }
  c.restore();
}

/** Water bubbles in the glass: [x, y, size] (r units), pale rims and a spec each. */
function waterBubbles(c, r, list, rim, tint) {
  for (var i = 0; i < list.length; i++) {
    glassBubble(c, list[i][0] * r, list[i][1] * r, list[i][2] * r, rim, tint, 0.95);
  }
}

// ---------------------------------------------------------------------------------------
// Tier 0 — Cell: a wide wobbly cell seen from above, no face. A green membrane band (deeper at
// the bottom, like the cell's thickness) with pale membrane lines, bright lime cytoplasm with
// soft organelle dots and a pale rim (as in the original), a green nucleus ring and a lime
// bean of a nucleus with darker spots and a glossy highlight.

var CELL_BAND = tone("#0f7a44", "#1ea864", "#34c47c", "#4cd490", "#26b06e");
var CELL_GOO = tone("#eeffc0", "#8cef88", "#c2fda6", "#e6ff9c", "#9cf28e");
var CELL_RING = tone("#178a40", "#34b452", "#47cc63", "#62d878", "#3cbc58");
var CELL_CORE = tone("#56a82a", "#bcef52", "#d8ff68", "#f0ff98", "#c6f65c");

var CELL_OUTER = wobblePts(0.01, -0.005, 0.885, 0.75, [[0.012, 3, 0.4], [0.008, 5, 1.9]], 64);
var CELL_MEMBRANE = wobblePts(0.01, -0.005, 0.855, 0.718, [[0.012, 3, 0.4], [0.008, 5, 1.9]], 64);
/** A second, fainter membrane line low in the band (the original's double rim). */
var CELL_MEMBRANE2 = wobblePts(0.01, -0.035, 0.81, 0.69, [[0.012, 3, 0.4], [0.008, 5, 1.9]], 64);

/** The cytoplasm sits high in the band and bulges in soft lumps along its top edge. */
var CELL_PLASM = (function () {
  var out = [], n = 80;
  for (var i = 0; i < n; i++) {
    var a = (i / n) * TAU, s = Math.sin(a);
    var k = 1 + (0.026 * Math.sin(a * 4 + 0.8) + 0.018 * Math.sin(a * 7 + 2.2)) * (s < 0 ? 1 : 0.35);
    out.push([0.015 + Math.cos(a) * 0.78 * k, -0.065 + s * 0.63 * k]);
  }
  return out;
})();
var CELL_NUC = [0.015, 0.0, 0.435, 0.36];
var CELL_RING_PTS = wobblePts(CELL_NUC[0], CELL_NUC[1], CELL_NUC[2], CELL_NUC[3], [[0.015, 3, 2.4], [0.01, 5, 0.6]], 48);
/** The nucleus bean: a big lobe upper left, pinched in at the lower right like the original's. */
var CELL_CORE_PTS = [
  [-0.34, -0.05], [-0.29, -0.2], [-0.14, -0.29], [0.06, -0.3], [0.24, -0.25], [0.35, -0.13],
  [0.36, 0.01], [0.28, 0.09], [0.16, 0.06], [0.07, 0.14], [-0.08, 0.19], [-0.26, 0.13],
];

/** Organelle dots [x, y, size, kind] in the cytoplasm: kind 0 pale bubble, 1 green speck. */
var CELL_DOTS = (function () {
  var out = [];
  for (var k = 0; k < 1200 && out.length < 24; k++) {
    var x = hash(k * 2.31 + 0.17) * 1.5 - 0.74, y = hash(k * 4.77 + 1.33) * 1.2 - 0.66;
    var s = 0.022 + hash(k * 6.13 + 2.9) * 0.034;
    // Inside the cytoplasm (with a margin), outside the nucleus ring.
    var ex = (x - 0.015) / (0.7 - s), ey = (y + 0.065) / (0.55 - s);
    if (ex * ex + ey * ey > 1) continue;
    var nx = (x - CELL_NUC[0]) / (CELL_NUC[2] + s + 0.035), ny = (y - CELL_NUC[1]) / (CELL_NUC[3] + s + 0.035);
    if (nx * nx + ny * ny < 1) continue;
    var clash = false;
    for (var j = 0; j < out.length && !clash; j++) {
      if (Math.hypot(out[j][0] - x, out[j][1] - y) < out[j][2] + s + 0.035) clash = true;
    }
    if (clash) continue;
    out.push([x, y, s, hash(k * 9.7 + 0.4) < 0.66 ? 0 : 1]);
  }
  return out;
})();

export var cell = {
  disc: { base: "#2fb35f", light: "#a4eeb0", shade: "#168649", ink: "#0e6436" },
  draw: function (c, r) {
    // Outer membrane band, its pale membrane lines.
    part(c, r, ptsPath(CELL_OUTER, r), CELL_BAND, r * 0.8, {
      cx: 0.01 * r, cy: 0, lightK: 0.92, rim: 0.15,
      inside: function (cc) {
        cc.lineWidth = r * 0.018;
        cc.strokeStyle = "rgba(196,248,160,0.95)";
        cc.beginPath();
        smoothPath(cc, CELL_MEMBRANE, r);
        cc.stroke();
        cc.lineWidth = r * 0.012;
        cc.strokeStyle = "rgba(196,248,160,0.45)";
        cc.beginPath();
        smoothPath(cc, CELL_MEMBRANE2, r);
        cc.stroke();
      },
    });
    // Cytoplasm with its organelle dots.
    part(c, r, ptsPath(CELL_PLASM, r), CELL_GOO, r * 0.6, {
      line: LINE.fine, swell: 0, cx: 0.015 * r, cy: -0.065 * r, light: false, rim: 0.4,
      inside: function (cc) {
        var g = cc.createLinearGradient(-0.55 * r, -0.62 * r, 0.5 * r, 0.55 * r);
        g.addColorStop(0, "rgba(236,255,150,0.95)");
        g.addColorStop(0.45, "rgba(206,255,170,0.55)");
        g.addColorStop(1, "rgba(150,240,140,0)");
        cc.fillStyle = g;
        cc.fillRect(-r, -r, 2 * r, 2 * r);
        for (var i = 0; i < CELL_DOTS.length; i++) {
          var d = CELL_DOTS[i], x = d[0] * r, y = d[1] * r, s = d[2] * r;
          cc.fillStyle = d[3] ? "rgba(112,206,112,0.75)" : "rgba(226,252,206,0.92)";
          cc.beginPath();
          cc.arc(x, y, s, 0, TAU);
          cc.fill();
          cc.fillStyle = d[3] ? "rgba(190,240,170,0.7)" : "rgba(255,255,255,0.7)";
          cc.beginPath();
          cc.arc(x - s * 0.3, y - s * 0.32, s * 0.34, 0, TAU);
          cc.fill();
        }
      },
    });
    // Nucleus ring, then the lime bean inside it.
    part(c, r, ptsPath(CELL_RING_PTS, r), CELL_RING, r * 0.4, {
      line: LINE.part, cx: CELL_NUC[0] * r, cy: CELL_NUC[1] * r, lightK: 0.84, rim: 0.3,
    });
    part(c, r, ptsPath(CELL_CORE_PTS, r), CELL_CORE, r * 0.3, {
      line: LINE.fine, cx: 0.01 * r, cy: -0.06 * r, lightK: 0.66, rim: 0.4,
      inside: function (cc) {
        var spots = [[0.22, -0.13, 0.085, 0.075], [-0.12, 0.06, 0.075, 0.052], [0.04, -0.14, 0.03, 0.026], [-0.22, -0.08, 0.026, 0.022], [0.08, 0.05, 0.028, 0.022]];
        for (var i = 0; i < spots.length; i++) {
          var s = spots[i];
          cc.fillStyle = "rgba(120,212,70,0.85)";
          cc.beginPath();
          cc.ellipse(s[0] * r, s[1] * r, s[2] * r, s[3] * r, 0, 0, TAU);
          cc.fill();
          cc.fillStyle = "rgba(218,248,146,0.8)";
          cc.beginPath();
          cc.ellipse((s[0] - s[2] * 0.25) * r, (s[1] - s[3] * 0.3) * r, s[2] * 0.42 * r, s[3] * 0.36 * r, -0.4, 0, TAU);
          cc.fill();
        }
      },
    });
    spot(c, -0.14 * r, -0.18 * r, 0.085 * r, 0.04 * r, -0.32);
    spot(c, -0.01 * r, -0.235 * r, 0.022 * r, 0.016 * r, 0, 0.85);
  },
};

// ---------------------------------------------------------------------------------------
// Tier 1 — Starfish: a fat, wide red five-point star (one arm straight up, the side arms
// nearly level) with a raised darker rim, a coral middle glowing peach round the face, cream
// dots and a small cute face.

/** Arms [angle (deg, 0 = right, 90 = down), reach]: a smooth union of tapered capsules. */
var STAR_ARMS = [[-90, 0.9], [-10, 0.9], [46, 0.885], [134, 0.885], [190, 0.9]];
var STAR_TIP = 0.15, STAR_ROOT = 0.44, STAR_CY = 0.015;

function starSdf(x, y) {
  var py = y - STAR_CY, d = 0;
  for (var i = 0; i < STAR_ARMS.length; i++) {
    var a = (STAR_ARMS[i][0] * Math.PI) / 180, L = STAR_ARMS[i][1] - STAR_TIP;
    var di = capsuleSdf(x, py, 0, 0, STAR_ROOT, Math.cos(a) * L, Math.sin(a) * L, STAR_TIP);
    d = i ? smin(d, di, 0.1) : di;
  }
  return d;
}

var STAR_OUT = sdfOutline(starSdf, 220, 1.0);
/** The coral middle, inset from the rim. */
var STAR_IN = sdfOutline(function (x, y) { return starSdf(x, y) + 0.068; }, 220, 1.0);
var STAR_RIM = tone("#a8142a", "#d02834", "#e6363f", "#ff5a5e", "#c92a36");
var STAR_MID = tone("#d2303e", "#f2464e", "#ff5e60", "#ff7e76", "#ec4a52");
var STAR_FACE = { x: 0.0, y: -0.04 };

/** Cream dots [x, y, size]: bigger ones along the arms, small ones scattered, none on the face. */
var STAR_DOTS = (function () {
  var out = [];
  function free(x, y, s) {
    if (starSdf(x, y) > -0.095 - s) return false;
    var fx = (x - STAR_FACE.x) / 0.46, fy = (y - STAR_FACE.y - 0.04) / 0.26;
    if (fx * fx + fy * fy < 1) return false;
    for (var j = 0; j < out.length; j++) {
      if (Math.hypot(out[j][0] - x, out[j][1] - y) < out[j][2] + s + 0.022) return false;
    }
    return true;
  }
  var along = [[0.44, 0.07, 0.042], [0.6, -0.05, 0.036], [0.72, 0.03, 0.03], [0.52, -0.13, 0.024], [0.36, 0.17, 0.026]];
  for (var i = 0; i < STAR_ARMS.length; i++) {
    var a = (STAR_ARMS[i][0] * Math.PI) / 180, ax = Math.cos(a), ay = Math.sin(a);
    for (var k = 0; k < along.length; k++) {
      var t = along[k][0], o = along[k][1] * (i % 2 ? -1 : 1), s = along[k][2];
      var x = ax * t - ay * o, y = STAR_CY + ay * t + ax * o;
      if (free(x, y, s)) out.push([x, y, s]);
    }
  }
  for (var n = 0; n < 1500 && out.length < 72; n++) {
    var qx = hash(n * 3.17 + 0.71) * 1.8 - 0.9, qy = hash(n * 5.31 + 2.13) * 1.8 - 0.9;
    var qs = 0.01 + hash(n * 7.7 + 1.3) * 0.014;
    if (free(qx, qy, qs)) out.push([qx, qy, qs]);
  }
  return out;
})();

/** Bubble rings in the deep blue water round the star (where the original has them). */
var STAR_BUBBLES = [
  [0.5, -0.7, 0.07], [0.37, -0.77, 0.03], [0.7, -0.5, 0.058], [-0.62, -0.52, 0.056],
  [-0.42, -0.74, 0.03], [-0.5, -0.66, 0.02], [-0.8, 0.3, 0.046], [-0.84, 0.42, 0.022],
  [0.8, 0.3, 0.05], [0.84, 0.42, 0.022], [0.02, 0.8, 0.05], [-0.1, 0.85, 0.022],
  [-0.36, 0.84, 0.026], [0.38, 0.86, 0.024],
];

export var starfish = {
  disc: { base: "#1d7fe2", light: "#7cc8ff", shade: "#0b4db2", ink: "#08398a" },
  discPattern: function (c, r) {
    waterBubbles(c, r, STAR_BUBBLES, "rgba(170,232,255,0.95)", "#9fdcff");
  },
  draw: function (c, r, pose) {
    var star = function (cc) { smoothPath(cc, STAR_OUT, r); };
    // The raised rim (the whole star), then the coral middle on it.
    part(c, r, star, STAR_RIM, r * 0.55, { cx: 0, cy: STAR_CY * r, lightK: 0.9, rim: 0.3 });
    part(c, r, function (cc) { smoothPath(cc, STAR_IN, r); }, STAR_MID, r * 0.42, {
      line: LINE.fine * 0.6, swell: 0, cx: 0, cy: STAR_CY * r, lightK: 0.86, rim: 0.28,
      inside: function (cc) {
        // A peach glow round the face, like the original's paler middle.
        var fx = STAR_FACE.x * r, fy = (STAR_FACE.y + 0.05) * r;
        var g = cc.createRadialGradient(fx, fy, 0, fx, fy, 0.58 * r);
        g.addColorStop(0, "rgba(255,216,184,1)");
        g.addColorStop(0.35, "rgba(255,188,152,0.92)");
        g.addColorStop(0.7, "rgba(255,130,112,0.4)");
        g.addColorStop(1, "rgba(255,120,110,0)");
        cc.fillStyle = g;
        cc.fillRect(fx - 0.6 * r, fy - 0.6 * r, 1.2 * r, 1.2 * r);
        for (var i = 0; i < STAR_DOTS.length; i++) {
          var d = STAR_DOTS[i], x = d[0] * r, y = d[1] * r, s = d[2] * r;
          cc.fillStyle = "rgba(236,150,90,0.55)";
          cc.beginPath();
          cc.arc(x + s * 0.12, y + s * 0.16, s * 1.08, 0, TAU);
          cc.fill();
          cc.fillStyle = "#ffdc8e";
          cc.beginPath();
          cc.arc(x, y, s, 0, TAU);
          cc.fill();
          if (s > 0.02 * r) {
            cc.fillStyle = "rgba(255,250,224,0.95)";
            cc.beginPath();
            cc.arc(x - s * 0.3, y - s * 0.32, s * 0.36, 0, TAU);
            cc.fill();
          }
        }
      },
    });
    // Gloss on the top arm and along the upper-left arm.
    spot(c, -0.015 * r, -0.68 * r, 0.03 * r, 0.05 * r, 0, 0.85);
    spot(c, -0.64 * r, -0.12 * r, 0.065 * r, 0.022 * r, -0.12, 0.7);
    kawaiiFace(c, r, {
      x: STAR_FACE.x, y: STAR_FACE.y, gap: 0.5, ex: 0.088, ey: 0.098,
      mouth: [STAR_FACE.x, 0.035, 0.074], blush: [[-0.43, 0.075], [0.43, 0.075]], br: [0.088, 0.056],
      eyeInk: "#3a0e14", mouthInk: "#5a1418", blushInk: "#ff6f90", skin: "#ffc4a4", spark2: true,
    }, pose);
  },
};

// ---------------------------------------------------------------------------------------
// Tier 2 — Jellyfish: a wide glossy pink dome with plump rounded corners and a soft wavy lip,
// a simple face, and six simple round-tipped tentacles hanging under it like an upside-down
// bouquet.

var JELLY_BELL = tone("#dc4a82", "#ff7cae", "#ff95c0", "#ffbbd6", "#f877aa");
var JELLY_ARM = tone("#d84680", "#ff76aa", "#ff8fbd", "#ffbdd8", "#f272a6");
var JELLY_X = 0.02;

/** Height of the bell's bottom edge at offset u from its middle: it dips at the corners. */
function bellBottomY(u) {
  return 0.09 + 0.035 * (u / 0.5) * (u / 0.5);
}

function bellPath(r) {
  return function (c) {
    var x = JELLY_X;
    c.moveTo((x - 0.6) * r, 0.02 * r);
    c.bezierCurveTo((x - 0.75) * r, -0.4 * r, (x - 0.42) * r, -0.73 * r, x * r, -0.73 * r);
    c.bezierCurveTo((x + 0.42) * r, -0.73 * r, (x + 0.75) * r, -0.4 * r, (x + 0.6) * r, 0.02 * r);
    // A plump rounded corner curling under into the bottom edge.
    c.bezierCurveTo((x + 0.63) * r, 0.09 * r, (x + 0.6) * r, 0.13 * r, (x + 0.5) * r, bellBottomY(0.5) * r);
    // The bottom edge: four soft scallops, right to left.
    var n = 4, w = 1.0 / n, k = 0.04;
    for (var i = 0; i < n; i++) {
      var u0 = 0.5 - w * i, u1 = u0 - w;
      c.bezierCurveTo((x + u0 - w * 0.1) * r, (bellBottomY(u0) + k) * r, (x + u1 + w * 0.1) * r,
        (bellBottomY(u1) + k) * r, (x + u1) * r, bellBottomY(u1) * r);
    }
    c.bezierCurveTo((x - 0.6) * r, 0.13 * r, (x - 0.63) * r, 0.09 * r, (x - 0.6) * r, 0.02 * r);
    c.closePath();
  };
}

/** Top of the light lip band: five puffy bumps across, dipping out of the bell at the sides. */
function lipTop(c, r) {
  var x = JELLY_X, n = 5, w = 1.04 / n, k = -0.035;
  var y = function (u) { return 0.03 + 0.025 * (u / 0.52) * (u / 0.52); };
  c.moveTo((x - 0.72) * r, -0.02 * r);
  c.quadraticCurveTo((x - 0.6) * r, 0.05 * r, (x - 0.52) * r, y(-0.52) * r);
  for (var i = 0; i < n; i++) {
    var u0 = -0.52 + w * i, u1 = u0 + w;
    c.bezierCurveTo((x + u0 + w * 0.1) * r, (y(u0) + k) * r, (x + u1 - w * 0.1) * r,
      (y(u1) + k) * r, (x + u1) * r, y(u1) * r);
  }
  c.quadraticCurveTo((x + 0.6) * r, 0.05 * r, (x + 0.72) * r, -0.02 * r);
}

/**
 * Tentacles, traced from the original: flat lobes that hang straight from the bell and widen
 * into big round tips, packed side by side with only an ink line between them. The outer pair
 * hooks out to the side with smaller, higher tips. Left half, back to front, mirrored for the
 * right; x from the jellyfish's middle. Each: outline points (a closed smooth curve; the tops
 * hide under the bell's lip), a pale streak [from, via, to, width], bubbles [x, y, radius] and
 * optionally a turn [angle, pivot x, pivot y] (rad, + swings the tip outward).
 */
var JELLY_TENTACLES_LEFT = [
  {
    // The second pair, mostly behind its neighbours: a strip, and a tip whose edge runs on
    // into the middle lobe's, so the two read as one big lobe at the bottom.
    pts: [[-0.265, 0.05], [-0.146, 0.05], [-0.146, 0.25], [-0.16, 0.442], [-0.2, 0.477], [-0.248, 0.44],
      [-0.28, 0.389], [-0.292, 0.325], [-0.28, 0.25]],
    streak: [[-0.256, 0.153], [-0.26, 0.25], [-0.262, 0.346], 0.04],
    dots: [[-0.24, 0.421, 0.012]],
    // Tilted about 5° outward from its root.
    turn: [0.087, -0.205, 0.05],
  },
  {
    // The outer pair: straight down on the inside, a diagonal outer edge, a round tip turned out.
    pts: [[-0.28, 0.05], [-0.28, 0.25], [-0.3, 0.39], [-0.35, 0.426], [-0.405, 0.434], [-0.455, 0.423],
      [-0.49, 0.39], [-0.5, 0.34], [-0.49, 0.29], [-0.46, 0.236], [-0.41, 0.186], [-0.38, 0.132], [-0.37, 0.05]],
    streak: [[-0.355, 0.153], [-0.395, 0.27], [-0.45, 0.37], 0.05],
    dots: [[-0.33, 0.3, 0.018], [-0.345, 0.36, 0.011]],
  },
  {
    // The middle pair: the widest lobes, covering the second pair's tips, the right one lapping
    // a little over the left.
    pts: [[-0.151, 0.05], [0, 0.05], [0, 0.25], [0.017, 0.335], [0.024, 0.41], [0.011, 0.464], [-0.028, 0.496],
      [-0.084, 0.504], [-0.14, 0.494], [-0.18, 0.469], [-0.205, 0.421], [-0.21, 0.357], [-0.19, 0.282], [-0.165, 0.207]],
    streak: [[-0.126, 0.143], [-0.138, 0.261], [-0.16, 0.389], 0.05],
    dots: [[-0.08, 0.314, 0.019], [-0.112, 0.37, 0.017], [-0.056, 0.426, 0.013]],
  },
];

/** A tentacle from the left half, turned, or its mirror image for the right half (side +1). */
function jellyTentacle(t, side) {
  var turn = t.turn || [0, 0, 0], ca = Math.cos(turn[0]), sa = Math.sin(turn[0]), m = side < 0 ? 1 : -1;
  var place = function (q) {
    var dx = q[0] - turn[1], dy = q[1] - turn[2];
    return [m * (turn[1] + dx * ca - dy * sa), turn[2] + dx * sa + dy * ca].concat(q.slice(2));
  };
  return {
    pts: t.pts.map(place),
    streak: [place(t.streak[0]), place(t.streak[1]), place(t.streak[2]), t.streak[3]],
    dots: t.dots.map(place),
  };
}

/** Back to front: each pair's left then right, so the right middle lobe laps over the left. */
var JELLY_TENTACLES = (function () {
  var out = [];
  for (var i = 0; i < JELLY_TENTACLES_LEFT.length; i++) {
    out.push(jellyTentacle(JELLY_TENTACLES_LEFT[i], -1), jellyTentacle(JELLY_TENTACLES_LEFT[i], 1));
  }
  return out;
})();

/** Tiny plankton specks [x, y, size, alpha] in the teal glass. */
var JELLY_SPECKS = (function () {
  var out = [];
  for (var k = 0; k < 400 && out.length < 64; k++) {
    var x = hash(k * 1.91 + 0.33) * 1.9 - 0.95, y = hash(k * 3.73 + 1.21) * 1.9 - 0.95;
    if (Math.hypot(x, y) > 0.9) continue;
    out.push([x, y, 0.006 + hash(k * 5.3 + 0.7) * 0.012, 0.35 + hash(k * 8.1 + 2.2) * 0.45]);
  }
  return out;
})();

export var jellyfish = {
  disc: { base: "#20c2bb", light: "#a0f3e6", shade: "#0c8b90", ink: "#08646c" },
  discPattern: function (c, r) {
    for (var i = 0; i < JELLY_SPECKS.length; i++) {
      var s = JELLY_SPECKS[i];
      c.fillStyle = "rgba(230,255,250," + s[3] + ")";
      c.beginPath();
      c.arc(s[0] * r, s[1] * r, s[2] * r, 0, TAU);
      c.fill();
    }
    waterBubbles(c, r, [[0.76, 0.4, 0.04], [0.8, 0.28, 0.022], [-0.78, 0.36, 0.03], [0.66, -0.6, 0.026], [-0.56, -0.66, 0.022], [0.12, 0.84, 0.026]],
      "rgba(210,255,248,0.9)", "#c8fff6");
  },
  draw: function (c, r, pose) {
    var i;
    // The tentacles: flat lobes with one ink line around the bunch and between neighbours,
    // a soft shadow under the bell, a pale streak down each and a few bubbles.
    var pen = LINE.part * r;
    c.save();
    c.translate(JELLY_X * r, 0);
    c.lineJoin = "round";
    c.lineCap = "round";
    c.strokeStyle = JELLY_ARM.ink;
    c.lineWidth = pen * 2;
    for (i = 0; i < JELLY_TENTACLES.length; i++) {
      c.beginPath();
      smoothPath(c, JELLY_TENTACLES[i].pts, r);
      c.stroke();
    }
    for (i = 0; i < JELLY_TENTACLES.length; i++) {
      var t = JELLY_TENTACLES[i], k = t.streak, j;
      c.save();
      c.beginPath();
      smoothPath(c, t.pts, r);
      // Fading lighter toward the tips, like the original's.
      var fill = c.createLinearGradient(0, 0.12 * r, 0, 0.5 * r);
      fill.addColorStop(0, JELLY_ARM.base);
      fill.addColorStop(1, mixHex(JELLY_ARM.base, JELLY_ARM.light, 0.55));
      c.fillStyle = fill;
      c.fill();
      c.clip();
      var shadow = c.createLinearGradient(0, 0.08 * r, 0, 0.2 * r);
      shadow.addColorStop(0, rgba(JELLY_ARM.ink, 0.35));
      shadow.addColorStop(1, rgba(JELLY_ARM.ink, 0));
      c.fillStyle = shadow;
      c.fillRect(-0.6 * r, 0, 1.2 * r, 0.22 * r);
      c.strokeStyle = "rgba(255,222,236,0.75)";
      c.lineWidth = k[3] * r;
      c.beginPath();
      c.moveTo(k[0][0] * r, k[0][1] * r);
      c.quadraticCurveTo(k[1][0] * r, k[1][1] * r, k[2][0] * r, k[2][1] * r);
      c.stroke();
      c.restore();
      c.strokeStyle = JELLY_ARM.ink;
      c.lineWidth = pen;
      c.beginPath();
      smoothPath(c, t.pts, r);
      c.stroke();
      for (j = 0; j < t.dots.length; j++) {
        var d = t.dots[j];
        spot(c, d[0] * r, d[1] * r, d[2] * r, d[2] * r, 0, 0.8);
      }
    }
    c.restore();
    var bell = bellPath(r);
    part(c, r, bell, JELLY_BELL, r * 0.62, {
      cx: JELLY_X * r, cy: -0.3 * r, lightK: 0.78, rim: 0.3,
      inside: function (cc) {
        // The pale glow under the face, like the original's milky middle.
        var g = cc.createRadialGradient(0.04 * r, -0.16 * r, 0, 0.04 * r, -0.16 * r, 0.5 * r);
        g.addColorStop(0, "rgba(255,238,246,0.85)");
        g.addColorStop(0.55, "rgba(255,214,232,0.45)");
        g.addColorStop(1, "rgba(255,200,224,0)");
        cc.fillStyle = g;
        cc.fillRect(-0.6 * r, -0.7 * r, 1.3 * r, 1.0 * r);
        // The plump light lip along the bottom, with a deeper line along its puffy top.
        cc.fillStyle = "rgba(255,196,222,0.95)";
        cc.beginPath();
        lipTop(cc, r);
        cc.lineTo(0.8 * r, 0.3 * r);
        cc.lineTo(-0.8 * r, 0.3 * r);
        cc.closePath();
        cc.fill();
        cc.lineCap = "round";
        cc.strokeStyle = rgba(JELLY_BELL.ink, 0.5);
        cc.lineWidth = r * 0.013;
        cc.beginPath();
        lipTop(cc, r);
        cc.stroke();
        // Small glossy dots on the dome.
        var dots = [[0.46, -0.46, 0.022], [0.54, -0.34, 0.016], [0.56, -0.18, 0.014], [0.14, -0.66, 0.018], [-0.54, -0.12, 0.014], [0.34, -0.58, 0.012]];
        for (var d = 0; d < dots.length; d++) spot(cc, dots[d][0] * r, dots[d][1] * r, dots[d][2] * r, dots[d][2] * r, 0, 0.7);
      },
    });
    spot(c, -0.32 * r, -0.57 * r, 0.085 * r, 0.04 * r, -0.5);
    spot(c, -0.5 * r, -0.4 * r, 0.026 * r, 0.02 * r, -0.5, 0.85);
    kawaiiFace(c, r, {
      x: 0.034, y: -0.27, gap: 0.5, ex: 0.08, ey: 0.09,
      mouth: [0.034, -0.185, 0.066], blush: [[-0.38, -0.15], [0.45, -0.15]], br: [0.09, 0.055],
      eyeInk: "#48122a", mouthInk: "#64142e", blushInk: "#ff7cb0", skin: JELLY_BELL.base, spark2: true,
      clip: bell,
    }, pose);
  },
};

// ---------------------------------------------------------------------------------------
// Tier 3 — Fish: a big, wide, round yellow fish facing us, a cream belly band, an orange
// dorsal fin leaning back, a two-lobed tail, a little fin on each side and a wide small smile.

var FISH_BODY = tone("#d65a0a", "#f7a51e", "#ffca30", "#ffe266", "#f2a226");
var FISH_FIN = tone("#d0580a", "#f38c12", "#ffae1c", "#ffd04a", "#ec9418");
/** The body outline, traced from the original: a flattish top, a rounder, narrower bottom. */
var FISH_BODY_PTS = [
  [-0.07, -0.535], [0.18, -0.515], [0.4, -0.43], [0.57, -0.27], [0.65, -0.07], [0.65, 0.13],
  [0.59, 0.3], [0.48, 0.45], [0.3, 0.6], [0.08, 0.685], [-0.17, 0.69], [-0.42, 0.6],
  [-0.6, 0.46], [-0.715, 0.28], [-0.765, 0.08], [-0.74, -0.11], [-0.65, -0.28], [-0.5, -0.42],
  [-0.3, -0.505],
];
var FISH_C = [-0.06, 0.08];

function fishBody(r) {
  return ptsPath(FISH_BODY_PTS, r);
}

var FISH_DORSAL = [[-0.36, -0.38], [-0.3, -0.56], [-0.13, -0.71], [0.07, -0.785], [0.23, -0.765], [0.32, -0.67], [0.335, -0.54], [0.28, -0.4], [0.0, -0.32]];
var FISH_TAIL = [
  [0.5, -0.2], [0.57, -0.38], [0.67, -0.47], [0.77, -0.43], [0.83, -0.3], [0.81, -0.16],
  [0.71, -0.05], [0.8, 0.06], [0.85, 0.19], [0.83, 0.31], [0.74, 0.38], [0.6, 0.34], [0.53, 0.14],
];
var FISH_BACKFIN = [[-0.68, -0.05], [-0.79, -0.04], [-0.865, 0.04], [-0.865, 0.13], [-0.8, 0.21], [-0.68, 0.2]];
var FISH_SIDEFIN = [[0.36, 0.06], [0.48, 0.0], [0.62, 0.02], [0.7, 0.1], [0.7, 0.22], [0.62, 0.31], [0.5, 0.33], [0.4, 0.28], [0.34, 0.17]];

/** Darker rays across a fin, from a root to each tip (r units). */
function finRays(c, r, root, tips) {
  c.save();
  c.strokeStyle = rgba(FISH_FIN.ink, 0.5);
  c.lineWidth = r * 0.018;
  c.lineCap = "round";
  for (var i = 0; i < tips.length; i++) {
    c.beginPath();
    c.moveTo(root[0] * r, root[1] * r);
    c.lineTo(tips[i][0] * r, tips[i][1] * r);
    c.stroke();
  }
  c.restore();
}

export var fish = {
  disc: { base: "#1a9ff2", light: "#8ee6ff", shade: "#0a62cc", ink: "#08469e" },
  draw: function (c, r, pose) {
    // Fins behind the body.
    part(c, r, ptsPath(FISH_TAIL, r), FISH_FIN, r * 0.3, {
      line: LINE.part, cx: 0.7 * r, cy: -0.08 * r, lightK: 0.6,
      inside: function (cc) {
        finRays(cc, r, [0.56, -0.06], [[0.7, -0.41], [0.79, -0.3], [0.78, -0.15], [0.8, 0.18], [0.7, 0.3]]);
      },
    });
    part(c, r, ptsPath(FISH_DORSAL, r), FISH_FIN, r * 0.3, {
      line: LINE.part, cx: 0.02 * r, cy: -0.58 * r, lightK: 0.6,
      inside: function (cc) {
        finRays(cc, r, [0.0, -0.36], [[-0.14, -0.66], [0.04, -0.74], [0.2, -0.74], [0.32, -0.62]]);
      },
    });
    part(c, r, ptsPath(FISH_BACKFIN, r), FISH_FIN, r * 0.12, { line: LINE.part, cx: -0.8 * r, cy: 0.08 * r, lightK: 0.6 });
    // The round body with its cream belly band, back spots and gloss.
    var body = fishBody(r);
    part(c, r, body, FISH_BODY, r * 0.66, {
      cx: FISH_C[0] * r, cy: FISH_C[1] * r, lightK: 0.72, rim: 0.3,
      inside: function (cc) {
        cc.fillStyle = "rgba(255,244,190,0.88)";
        cc.beginPath();
        cc.ellipse(-0.07 * r, 0.8 * r, 0.68 * r, 0.36 * r, 0, 0, TAU);
        cc.fill();
        cc.strokeStyle = "rgba(255,255,236,0.85)";
        cc.lineWidth = r * 0.014;
        cc.beginPath();
        cc.ellipse(-0.07 * r, 0.8 * r, 0.68 * r, 0.36 * r, 0, Math.PI * 1.1, Math.PI * 1.9);
        cc.stroke();
        cc.fillStyle = "rgba(240,140,30,0.75)";
        var dots = [[0.42, -0.3, 0.024, 0.016], [0.51, -0.28, 0.02, 0.014], [0.46, -0.22, 0.017, 0.012], [0.55, -0.2, 0.014, 0.011]];
        for (var i = 0; i < dots.length; i++) {
          cc.beginPath();
          cc.ellipse(dots[i][0] * r, dots[i][1] * r, dots[i][2] * r, dots[i][3] * r, -0.5, 0, TAU);
          cc.fill();
        }
      },
    });
    spot(c, -0.45 * r, -0.31 * r, 0.09 * r, 0.05 * r, -0.6);
    spot(c, -0.6 * r, -0.15 * r, 0.022 * r, 0.018 * r, 0, 0.85);
    spot(c, -0.04 * r, -0.66 * r, 0.045 * r, 0.022 * r, -0.4, 0.75);
    kawaiiFace(c, r, {
      x: -0.167, y: 0.02, gap: 0.626, ex: 0.084, ey: 0.1,
      mouth: [-0.175, 0.105, 0.11], blush: [[-0.64, 0.17], [0.28, 0.17]], br: [0.1, 0.058],
      eyeInk: "#3a1608", mouthInk: "#8c240c", blushInk: "#ff7a7c", skin: FISH_BODY.base,
      clip: body,
    }, pose);
    // The little fin over the flank (in front of the cheek), waving.
    part(c, r, ptsPath(FISH_SIDEFIN, r), FISH_FIN, r * 0.17, {
      line: LINE.part, cx: 0.52 * r, cy: 0.16 * r, lightK: 0.6,
      inside: function (cc) {
        finRays(cc, r, [0.36, 0.16], [[0.64, 0.08], [0.68, 0.17], [0.64, 0.27]]);
      },
    });
  },
};

/** @type {Record<string, { disc: Object, discPattern?: Function, draw: Function }>} */
export var SEA_CREATURES = { cell: cell, starfish: starfish, jellyfish: jellyfish, fish: fish };
